/**
 * Artifact Card Block Renderer
 *
 * Receipt for a page the agent just authored via `create_artifact` or
 * rewrote via `update_artifact`: a card naming it, which opens the page in the
 * canvas. Rebuilt from the persisted tool-call arguments, so it renders the
 * same live and from history.
 *
 * A card opens the artifact where there is a canvas to open — the global chat.
 * Everywhere else it stays a display-only receipt.
 */

import { ArtifactCanvasSlot, ArtifactCard, artifactSlotId } from "@/components/artifact-canvas";
import { artifactIdOf, versionOf } from "@/components/artifact-canvas/artifactCardTarget";
import { ANALYTICS_EVENTS } from "@/lib/analytics/events";
import { useTrack } from "@/lib/analytics/useTrack";
import { useSlotCanvasStore } from "@/store/slotCanvasStore";
import { useSlotCanvasContext } from "../slot-canvas/SlotCanvasContext";
import type { UIBlock } from "@/types/ui-blocks";

export interface ArtifactCardBlockRendererProps {
  block: UIBlock;
}

export function ArtifactCardBlockRenderer({ block }: ArtifactCardBlockRendererProps) {
  const { mode } = useSlotCanvasContext();
  const openSlot = useSlotCanvasStore((state) => state.openSlot);
  const trackEvent = useTrack();
  const artifactId = block.type === "artifact_card" ? artifactIdOf(block.content) : "";
  const version = block.type === "artifact_card" ? versionOf(block.content.version) : null;
  const isOpen = useSlotCanvasStore(
    (state) => Boolean(artifactId) && state.openSlotId === artifactSlotId(artifactId, version),
  );

  if (block.type !== "artifact_card" || !artifactId) {
    return null;
  }

  const { title, description } = block.content;
  const interactive = mode === "canvas";

  return (
    <>
      <div className="mt-3.5">
        <ArtifactCard
          title={title}
          description={description}
          version={version}
          isOpen={isOpen}
          onOpen={
            interactive
              ? () => {
                  trackEvent(ANALYTICS_EVENTS.ARTIFACT_OPENED, { via: "chat_card" });
                  openSlot(artifactSlotId(artifactId, version));
                }
              : undefined
          }
        />
      </div>
      {interactive && (
        <ArtifactCanvasSlot artifactId={artifactId} title={title} version={version} />
      )}
    </>
  );
}
